import React, { useState, useEffect } from "react";
import "./TrainingMatrixReport.css";
import XLSXStyle from "xlsx-js-style";
import { saveAs } from "file-saver";
import DataTable from "./DataTable";
import { getDistinctFilters } from '../Components/Menu/Branch';
import { funcAreaAPI, trainingMatrixAPI } from "./api";

function TrainingMatrixReport() {
  const [branchName, setBranchName] = useState("");
  const [franchiseName, setFranchiseName] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [funcAreas, setFuncAreas] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFranchiseMap, setBranchFranchiseMap] = useState({});

  useEffect(() => {
    getDistinctFilters().then(({ franchises: f, branches: b, branchFranchiseMap: m }) => {
      setFranchises(f);
      setBranches(b);
      setBranchFranchiseMap(m || {});
    });
  }, []);

  const filteredFranchises = branchName && branchFranchiseMap[branchName]
    ? branchFranchiseMap[branchName]
    : franchises;

  const handleBranchChange = (e) => {
    setBranchName(e.target.value);
    setFranchiseName("");
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchName) params.branch = branchName;
      if (franchiseName) params.franchise_name = franchiseName;

      const [funcAreaData, empData] = await Promise.all([
        funcAreaAPI.getAll(),
        trainingMatrixAPI.getEmpFuncSubArea(params),
      ]);

      const areas = Array.isArray(funcAreaData) ? funcAreaData : [];
      const emps = Array.isArray(empData) ? empData : [];
      setFuncAreas(areas);

      const rows = emps.map((emp, idx) => {
        const empCodes = Array.isArray(emp.func_sub_area_codes)
          ? emp.func_sub_area_codes.map(String)
          : emp.func_sub_area_code != null
            ? [String(emp.func_sub_area_code)]
            : [];

        const areaCounts = {};
        let total = 0;
        areas.forEach((area) => {
          const key = String(area.func_sub_area_code);
          const count = empCodes.filter((c) => c === key).length;
          areaCounts[key] = count;
          total += count;
        });

        return {
          sr_no: idx + 1,
          sal_code: emp.sal_code,
          emp_name: emp.emp_name || "-",
          ...areaCounts,
          total,
        };
      });

      setTableData(rows);
      setShowTable(true);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBranchName("");
    setFranchiseName("");
    setShowTable(false);
    setTableData([]);
    setFuncAreas([]);
  };

  const handleExport = () => {
    console.log('[TMR Export] Starting export...');
    console.log('[TMR Export] funcAreas:', funcAreas.length, funcAreas.map(a => a.func_sub_area_code));
    console.log('[TMR Export] tableData rows:', tableData.length);

    const today = new Date().toLocaleDateString("en-IN", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });

    const PINK_FILL = { patternType: "solid", fgColor: { rgb: "F4B8C8" }, bgColor: { rgb: "F4B8C8" } };
    const HEADER_STYLE = { font: { bold: true }, fill: { patternType: "solid", fgColor: { rgb: "D9D9D9" } } };
    const DEFAULT_STYLE = { font: { bold: false } };
    const PINK_STYLE = { font: { bold: false }, fill: PINK_FILL };

    // Row 0: title, Row 1: date, Row 2: blank, Row 3: headers, Row 4+: data
    const DATA_START_ROW = 4;
    const AREA_START_COL = 3; // Sr No(0), Sal Code(1), Emp Name(2), then areas

    const headers = [
      "Sr No.", "Sal Code", "Employee Name",
      ...funcAreas.map((a) => String(a.func_sub_area_code)),
      "Total",
    ];

    const dataRows = tableData.map((row) => [
      row.sr_no,
      row.sal_code,
      row.emp_name,
      ...funcAreas.map((a) => row[String(a.func_sub_area_code)] ?? 0),
      row.total,
    ]);

    console.log('[TMR Export] Sample dataRow[0]:', dataRows[0]);

    // Count pink cells for debug
    let pinkCount = 0;

    // Build worksheet cell by cell for full style control
    const ws = {};
    const maxCol = headers.length - 1;
    const totalNoteRows = 2 + funcAreas.length + 2; // blank + "Note" + areas + blank + legend
    const maxRow = DATA_START_ROW + dataRows.length + totalNoteRows;

    const setCell = (r, c, value, style) => {
      const ref = XLSXStyle.utils.encode_cell({ r, c });
      const t = typeof value === 'number' ? 'n' : 's';
      ws[ref] = { v: value ?? '', t, s: style };
    };

    // Row 0: title
    setCell(0, 0, 'Report Title : Training Matrix Report', DEFAULT_STYLE);
    // Row 1: date
    setCell(1, 0, `Report Date : ${today}`, DEFAULT_STYLE);
    // Row 2: blank — skip
    // Row 3: headers
    headers.forEach((h, ci) => setCell(3, ci, h, HEADER_STYLE));

    // Data rows
    dataRows.forEach((row, ri) => {
      row.forEach((val, ci) => {
        const isAreaCol = ci >= AREA_START_COL && ci < AREA_START_COL + funcAreas.length;
        const isPink = isAreaCol && val === 0;
        if (isPink) pinkCount++;
        setCell(DATA_START_ROW + ri, ci, val, isPink ? PINK_STYLE : DEFAULT_STYLE);
      });
    });

    console.log('[TMR Export] Pink cells applied:', pinkCount);

    // Note section
    const noteStartRow = DATA_START_ROW + dataRows.length + 1;
    setCell(noteStartRow, 0, 'Note', { font: { bold: true } });
    funcAreas.forEach((a, i) => {
      setCell(noteStartRow + 1 + i, 0, String(a.func_sub_area_code), DEFAULT_STYLE);
      setCell(noteStartRow + 1 + i, 1, a.func_sub_area_name || '', DEFAULT_STYLE);
    });

    // Set worksheet range
    ws['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxRow, c: maxCol } });

    console.log('[TMR Export] ws ref:', ws['!ref']);
    console.log('[TMR Export] Sample pink cell check - D5 (first data area col):', ws[XLSXStyle.utils.encode_cell({ r: DATA_START_ROW, c: AREA_START_COL })]);

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Training Matrix Report");
    const buf = XLSXStyle.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "TrainingMatrixReport.xlsx");
    console.log('[TMR Export] Export complete.');
  };

  const columns = [
    { header: "Sr No.", accessor: (row) => row.sr_no },
    { header: "Sal Code", accessor: (row) => row.sal_code },
    { header: "Employee Name", accessor: (row) => row.emp_name },
    ...funcAreas.map((area) => ({
      header: area.func_sub_area_code || String(area.func_sub_area_code),
      accessor: (row) => row[String(area.func_sub_area_code)] ?? 0,
      cellStyle: (row) => {
        const val = row[String(area.func_sub_area_code)] ?? 0;
        return val === 0 ? { backgroundColor: '#f4b8c8' } : {};
      },
    })),
    { header: "Total", accessor: (row) => row.total },
  ];

  return (
    <div className="tmr-body">
      <div className="tmr-form">
        <div className="tmr-grid">
          <label className="filter-label">Branch Name</label>
          <select className="filter-input" value={branchName} onChange={handleBranchChange}>
            <option value="">-- Select --</option>
            {branches.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
          <div></div>

          <label className="filter-label">Franchise Name</label>
          <select className="filter-input" value={franchiseName} onChange={(e) => setFranchiseName(e.target.value)}>
            <option value="">-- Select --</option>
            {filteredFranchises.map((f) => (<option key={f} value={f}>{f}</option>))}
          </select>
          <div></div>
        </div>
      </div>

      <div className="tmr-action-bar">
        <button className="tmr-btn" onClick={handleViewReport} disabled={loading}>
          {loading ? "Loading…" : "View Report"}
        </button>
        <button className="tmr-btn tmr-btn-secondary" onClick={handleReset}>
          Reset
        </button>
        <button className="tmr-btn" onClick={handleExport}>Export To Excel</button>
      </div>

      {showTable && (
        <div className="tmr-table-section">
          <p className="tmr-report-date">
            Report Date:{" "}
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <DataTable
            columns={columns}
            data={tableData}
            showEditCols={false}
            emptyMessage="No data found"
            rowKey={(row, idx) => `${row.sal_code}-${idx}`}
          />
          {funcAreas.length > 0 && (
            <div className="tmr-note">
              <strong>Note</strong>
              <table className="tmr-note-table">
                <tbody>
                  {funcAreas.map((area) => (
                    <tr key={area.func_sub_area_code}>
                      <td className="tmr-note-code">{area.func_sub_area_code}</td>
                      <td className="tmr-note-name">{area.func_sub_area_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrainingMatrixReport;
